import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../shared/api';
import { API_BASE } from '../shared/constants';
import { orderStatusLabel, orderStatusTooltip } from '../shared/orderStatus';

type Locale = 'hr' | 'en';

type BucketKey = 'isporuceno' | 'imam_ju' | 'poslano' | 'na_putu' | 'pakirano' | 'age_0_10' | 'age_11_20' | 'age_21_plus' | 'na_cekanju' | 'nepoznato';
type GroupKey = 'smjesteno' | 'poslano' | 'age_0_10' | 'age_11_20' | 'age_21_plus' | 'na_cekanju' | 'nepoznato';
type Pogled = 'bis' | 'faros';

type Stat = { eur: number; count: number; pct: number; pct_count: number; fine_g: number; pct_fine: number };

type Summary = {
    as_of_batch: string | null;
    rekonstrukcija: boolean;
    pogled: Pogled;
    definicije: { amount: 'predujam' | 'iznos'; count: 'narudzbe' | 'stavke'; age_rule: 'bis' | 'faros'; stednja: 'bez' | 'samo' | null; cekanje: 'odvoji' | 'ne' };
    total_predujam_eur: number;
    total_narudzbi: number;
    total_iznos_eur: number;
    total_stavki: number;
    total_eur: number;
    total_count: number;
    total_fine_g: number;
    total_fine_ag_g: number;
    buckets: Record<BucketKey, Stat>;
    grupe: Record<GroupKey, Stat>;
    rok: { u_roku: Stat; izvan_roka: Stat; na_cekanju: Stat };
};

type OpenOrderRow = {
    order_number: string;
    sf: string;
    poslovnica: string | null;
    datum_unosa: string | null;
    starost_dana: number | null;
    iznos_eur: number;
    predujam_eur: number;
    preostalo_eur: number;
    status_bis: string | null;
    status_bis_label: string | null;
    handled: boolean;
    age_bucket: string | null;
    u_roku: boolean;
    rok_status: 'u_roku' | 'izvan_roka' | 'na_cekanju';
    na_cekanju: boolean;
    na_cekanju_razlog: 'dogovoren_datum' | 'klijent' | null;
    dogovoreni_datum: string | null;
    napomena: string | null;
    stavki: number;
    fine_g: number;
    fine_ag_g: number;
    zlatna_stednja: boolean;
    items: Array<{ naziv: string; kolicina: number; datum: string | null; metal: 'AU' | 'AG'; fine_g: number; fine_jed_g: number | null; fine_ag_g: number; fine_ag_jed_g: number | null }>;
};

type ListResp = {
    total: number;
    sum_predujam_eur: number;
    sum_iznos_eur: number;
    sum_stavki: number;
    sum_fine_g: number;
    sum_fine_ag_g: number;
    orders: OpenOrderRow[];
};

/**
 * m.40.3 dopuna — "Otvorene narudžbe po starosti" (mail UID 698/699, Ružica/Tonći
 * 21-22.09.2026). Zamjena za ručno izvlačenje iz Farosa u Excel: klikabilni sažetak
 * (status + starost) + filtrirana detaljna lista + XLSX export. Backend:
 * OpenOrdersAgingService / GoldOpenOrdersReportController — status se postavlja
 * preko postojećeg /api/offer-annotations ekrana (ovdje samo read + export).
 *
 * Prikaz (Tonći, "Obećanje kupcu, dodatak 1", 01.10.2026; usporedba DMS 722): isti snapshot
 * se može gledati po BIS definicijama (predujam, jedinstvene narudžbe, 10 dana još u roku,
 * sve poslovnice) ili kao Tonćijeva Faros tablica (puni iznos, stavke izvoza, dan narudžbe
 * je prvi dan, bez Zlatne štednje). "Na dan" rekonstruira stanje u prošlom trenutku.
 */
export function OpenOrdersAgingPanel({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const [summary, setSummary] = useState<Summary | null>(null);
    const [rows, setRows] = useState<OpenOrderRow[]>([]);
    const [total, setTotal] = useState(0);
    const [sumPredujam, setSumPredujam] = useState(0);
    const [sumIznos, setSumIznos] = useState(0);
    const [sumStavki, setSumStavki] = useState(0);
    const [sumFine, setSumFine] = useState(0);
    const [pogled, setPogled] = useState<Pogled>('bis');
    const [asOf, setAsOf] = useState(''); // '' = zadnji snapshot; inače 'YYYY-MM-DDTHH:MM' (datetime-local)
    const [status, setStatus] = useState<string>(''); // '' = svi, 'none' = neoznačeno, 'grupa:smjesteno' / 'grupa:poslano' = Tonćijeve grupe
    const [ageBucket, setAgeBucket] = useState<string>('');
    const [branch, setBranch] = useState('');
    const [search, setSearch] = useState('');
    const [sortKey, setSortKey] = useState<'order_number' | 'poslovnica' | 'datum_unosa' | 'starost_dana' | 'iznos_eur' | 'predujam_eur' | 'status_bis' | 'stavki' | 'fine_g'>('starost_dana');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    // N14 (Vedran 24.09.2026): klik na narudzbu otvara drawer s popisom artikala, bez kolicina.
    const [drawer, setDrawer] = useState<OpenOrderRow | null>(null);
    useEffect(() => {
        if (!drawer) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawer(null); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [drawer]);

    const faros = pogled === 'faros';
    // Mail UID 876, ispravak 5 (15.10.2026): zadani amount je od sada 'iznos' (puni iznos
    // narudzbe), ne 'predujam' - koja je kolona "primarna" (bold/odabrani zbroj) prati
    // stvarni amount iz backend odgovora, ne vise samo faros/bis preset.
    const amountIznos = summary?.definicije.amount !== 'predujam';
    const asOfParam = asOf ? asOf.replace('T', ' ') + (asOf.length === 16 ? ':00' : '') : '';

    const viewQs = useCallback((qs: URLSearchParams) => {
        if (pogled !== 'bis') qs.set('pogled', pogled);
        if (asOfParam) qs.set('as_of', asOfParam);
    }, [pogled, asOfParam]);

    // Brojaci zahtjeva: zakasnjeli odgovor starog prikaza ili as_of se odbacuje (audit 40.x, 6. tablica).
    const summaryReq = useRef(0);
    const listReq = useRef(0);

    const loadSummary = useCallback(async () => {
        const req = ++summaryReq.current;
        try {
            const qs = new URLSearchParams();
            viewQs(qs);
            if (branch.trim()) qs.set('branch', branch.trim());
            if (search.trim()) qs.set('search', search.trim());
            const data = await api<Summary>(`/gold-open-orders/summary${qs.toString() ? `?${qs}` : ''}`, token);
            if (req !== summaryReq.current) return;
            setSummary(data);
        } catch (ex) {
            setErr(ex instanceof Error ? ex.message : 'API');
        }
    }, [token, branch, search, viewQs]);

    const loadList = useCallback(async () => {
        const req = ++listReq.current;
        setBusy(true); setErr('');
        try {
            const qs = new URLSearchParams();
            viewQs(qs);
            if (status) qs.set('status', status);
            if (ageBucket) qs.set('age_bucket', ageBucket);
            if (branch.trim()) qs.set('branch', branch.trim());
            if (search.trim()) qs.set('search', search.trim());
            qs.set('sort', sortKey);
            qs.set('dir', sortDir);
            const data = await api<ListResp>(`/gold-open-orders?${qs}`, token);
            if (req !== listReq.current) return;
            setRows(data.orders ?? []);
            setTotal(data.total ?? 0);
            setSumPredujam(data.sum_predujam_eur ?? 0);
            setSumIznos(data.sum_iznos_eur ?? 0);
            setSumStavki(data.sum_stavki ?? 0);
            setSumFine(data.sum_fine_g ?? 0);
        } catch (ex) {
            setErr(ex instanceof Error ? ex.message : 'API');
        } finally {
            if (req === listReq.current) setBusy(false);
        }
    }, [token, status, ageBucket, branch, search, sortKey, sortDir, viewQs]);

    useEffect(() => { void loadSummary(); }, [loadSummary]);
    useEffect(() => { void loadList(); }, [loadList]);

    const toggleSort = (k: typeof sortKey) => {
        if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        else { setSortKey(k); setSortDir(k === 'order_number' || k === 'poslovnica' ? 'asc' : 'desc'); }
    };
    const sortArrow = (k: typeof sortKey) => (sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

    async function exportXlsx() {
        const qs = new URLSearchParams();
        viewQs(qs);
        if (status) qs.set('status', status);
        if (ageBucket) qs.set('age_bucket', ageBucket);
        if (branch.trim()) qs.set('branch', branch.trim());
        if (search.trim()) qs.set('search', search.trim());
        qs.set('sort', sortKey);
        qs.set('dir', sortDir);
        const res = await fetch(`${API_BASE}/gold-open-orders/export.xlsx?${qs}`, {
            headers: { Authorization: `Bearer ${token}`, Accept: '*/*' },
        });
        if (!res.ok) {
            setErr(res.status === 401 ? 'Sesija je istekla, prijavite se ponovno.' : `Izvoz nije uspio (${res.status}).`);
            return;
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        // Ime datoteke (s vremenom) iz Content-Disposition, kao ostali izvozi.
        const cd = res.headers.get('Content-Disposition') ?? '';
        const named = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd)?.[1];
        a.href = url; a.download = named ? decodeURIComponent(named) : 'otvorene_nr_po_starosti.xlsx';
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
    }

    const cardStyle = (active: boolean): React.CSSProperties => ({
        cursor: 'pointer', textAlign: 'left', border: active ? '2px solid var(--ad-ink)' : '1px solid rgba(var(--ad-soft-rgb),0.4)',
        borderRadius: 12, padding: '0.7rem 0.9rem', background: active ? 'rgba(254,243,199,0.6)' : '#fff',
        minWidth: 150, flex: '1 1 150px',
    });
    const fmtEur = (n: number) => n.toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const fmtPct = (n: number) => n.toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
    // Fine Au u gramima (Vedran 01.10.2026): EUR pliva s cijenom zlata, fine je uvijek isti.
    const fmtG = (n: number) => n.toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 3 });
    // Jedinica brojanja: preset Faros također broji narudžbe (Vedran 01.10.2026); stavke samo ako backend tako javi.
    const countStavke = summary?.definicije.count === 'stavke';
    const unit = countStavke ? t('stavki', 'lines') : t('nar.', 'ord.');
    const unitLong = countStavke ? t('stavki', 'lines') : t('narudžbi', 'orders');

    // Klik na karticu = postavi status/age_bucket filter (isključivo, klik ponovno = ukloni).
    const statusBuckets: BucketKey[] = ['isporuceno', 'imam_ju', 'poslano', 'na_putu', 'pakirano'];
    const ageOf = (key: string) => (key === 'age_0_10' ? '0-10' : key === 'age_11_20' ? '11-20' : key === 'age_21_plus' ? '21+' : key === 'na_cekanju' ? 'na_cekanju' : 'nepoznato');
    const clickBucket = (key: BucketKey) => {
        if (statusBuckets.includes(key)) {
            setAgeBucket('');
            setStatus((cur) => (cur === key ? '' : key));
        } else {
            const bucket = ageOf(key);
            setStatus('');
            setAgeBucket((cur) => (cur === bucket ? '' : bucket));
        }
    };
    const isActive = (key: BucketKey): boolean => (statusBuckets.includes(key) ? status === key : ageBucket === ageOf(key));
    const clickGroup = (key: GroupKey) => {
        if (key === 'smjesteno' || key === 'poslano') {
            setAgeBucket('');
            setStatus((cur) => (cur === `grupa:${key}` ? '' : `grupa:${key}`));
        } else {
            const bucket = ageOf(key);
            setStatus('');
            setAgeBucket((cur) => (cur === bucket ? '' : bucket));
        }
    };
    const isGroupActive = (key: GroupKey): boolean => ((key === 'smjesteno' || key === 'poslano') ? status === `grupa:${key}` : ageBucket === ageOf(key));

    const bucketLabels: Record<BucketKey, string> = {
        isporuceno: t('Greška u Farosu — već isporučeno', 'Faros error — already delivered'),
        imam_ju: orderStatusLabel('imam_ju', locale),
        poslano: orderStatusLabel('poslano', locale),
        na_putu: orderStatusLabel('na_putu', locale),
        pakirano: orderStatusLabel('pakirano', locale),
        age_0_10: t('Do 10 dana', 'Up to 10 days'),
        age_11_20: t('11-20 dana', '11-20 days'),
        age_21_plus: t('Više od 21 dan', 'More than 21 days'),
        na_cekanju: t('Na čekanju (rok produljen)', 'On hold (deadline extended)'),
        nepoznato: t('Nepoznata starost', 'Unknown age'),
    };
    // Grupe kao u Tonćijevoj Faros tablici (nazivi iz njegova maila 30.09./01.10.2026).
    const groupLabels: Record<GroupKey, string> = {
        smjesteno: t('IMAM JU / Smješteno', 'IMAM JU / Placed'),
        poslano: t('Poslano', 'Sent'),
        age_0_10: t('Datum narudžbe do 10 dana', 'Order date up to 10 days'),
        age_11_20: t('Od 11 do 20 dana', '11 to 20 days'),
        age_21_plus: t('Više od 21 dan', 'More than 21 days'),
        na_cekanju: t('Na čekanju (rok produljen)', 'On hold (deadline extended)'),
        nepoznato: t('Nepoznata starost', 'Unknown age'),
    };

    const defLine = summary ? [
        summary.definicije.amount === 'iznos' ? t('puni iznos narudžbe', 'full order amount') : t('uplaćeni predujam', 'advance paid'),
        summary.definicije.count === 'stavke' ? t('broj = stavke Faros izvoza', 'count = Faros export lines') : t('broj = jedinstvene narudžbe', 'count = unique orders'),
        summary.definicije.age_rule === 'faros' ? t('dan narudžbe je 1. dan (10 dana = izvan roka)', 'order day counts as day 1 (10 days = overdue)') : t('starost = razlika dana (10 dana = u roku)', 'age = day difference (10 days = on time)'),
        summary.definicije.stednja === 'bez' ? t('bez Zlatne štednje', 'without Zlatna štednja') : summary.definicije.stednja === 'samo' ? t('samo Zlatna štednja', 'Zlatna štednja only') : t('sve poslovnice', 'all branches'),
    ].join(' · ') : '';

    const inputStyle: React.CSSProperties = { padding: '6px 10px', borderRadius: 8 };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.6rem', flexWrap: 'wrap', gap: 8 }}>
                <h3 style={{ margin: 0 }}>{t('Otvorene narudžbe po starosti', 'Open orders by age')}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end', flex: '1 1 auto', minWidth: 0 }}>
                    <label style={{ fontSize: '0.78rem', opacity: 0.8 }}>{t('Prikaz', 'View')}</label>
                    <select value={pogled} onChange={(e) => { setPogled(e.target.value as Pogled); setStatus(''); setAgeBucket(''); }} style={inputStyle} title={t('BIS: iznos narudžbe, 10 dana je u roku, bez Zlatne štednje. Faros: puni iznos narudžbe, dan narudžbe je 1. dan, bez Zlatne štednje. Oba broje narudžbe.', 'BIS: order amount, 10 days is on time, without Zlatna štednja. Faros: full order amount, order day is day 1, without Zlatna štednja. Both count orders.')}>
                        <option value="bis">{t('BIS 40.3 (iznos narudžbe, bez Zlatne štednje)', 'BIS 40.3 (order amount, without Zlatna štednja)')}</option>
                        <option value="faros">{t('Kao Faros tablica (puni iznos, bez Zlatne štednje)', 'As Faros table (full amount, without Zlatna štednja)')}</option>
                    </select>
                    <label style={{ fontSize: '0.78rem', opacity: 0.8 }}>{t('Na dan', 'As of')}</label>
                    <input type="datetime-local" value={asOf} onChange={(e) => setAsOf(e.target.value)} style={inputStyle} title={t('Rekonstrukcija stanja u tom trenutku; prazno = zadnji snapshot.', 'Reconstruction at that moment; empty = latest snapshot.')} />
                    {asOf ? <button type="button" onClick={() => setAsOf('')} style={{ ...inputStyle, cursor: 'pointer' }}>{t('Sada', 'Now')}</button> : null}
                    {summary?.as_of_batch ? (
                        <span style={{ fontSize: '0.78rem', opacity: 0.7 }}>
                            {summary.rekonstrukcija ? t('Rekonstrukcija', 'Reconstruction') : t('Faros snapshot', 'Faros snapshot')}: {String(summary.as_of_batch).slice(0, 19).replace('T', ' ')}
                        </span>
                    ) : null}
                </div>
            </div>

            {err ? <p className="error">{err}</p> : null}

            {summary ? (
                <>
                    {faros ? <div style={{ fontSize: '0.76rem', opacity: 0.7, marginBottom: 6 }}>{defLine}</div> : null}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: '0.8rem' }}>
                        {faros
                            ? (Object.keys(groupLabels) as GroupKey[]).filter((key) => (key !== 'nepoznato' || summary.grupe.nepoznato.count > 0) && (key !== 'na_cekanju' || summary.grupe.na_cekanju.count > 0)).map((key) => {
                                const g = summary.grupe[key];
                                return (
                                    <button key={key} type="button" onClick={() => clickGroup(key)} style={cardStyle(isGroupActive(key))}>
                                        <div style={{ fontSize: '0.72rem', opacity: 0.75, marginBottom: 2 }}>{groupLabels[key]}</div>
                                        <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>{fmtEur(g.eur)} €</div>
                                        <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>{fmtG(g.fine_g)} g fine · {fmtPct(g.pct_fine)}</div>
                                        <div style={{ fontSize: '0.72rem', opacity: 0.7 }}>{fmtPct(g.pct)} · {g.count} {unit} · {fmtPct(g.pct_count)}</div>
                                    </button>
                                );
                            })
                            : (Object.keys(bucketLabels) as BucketKey[]).filter((key) => key !== 'na_cekanju' || summary.buckets.na_cekanju.count > 0).map((key) => {
                                const b = summary.buckets[key];
                                return (
                                    <button key={key} type="button" onClick={() => clickBucket(key)} style={cardStyle(isActive(key))}>
                                        <div style={{ fontSize: '0.72rem', opacity: 0.75, marginBottom: 2 }}>{bucketLabels[key]}</div>
                                        <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>{fmtEur(b.eur)} €</div>
                                        <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>{fmtG(b.fine_g)} g fine · {fmtPct(b.pct_fine)}</div>
                                        <div style={{ fontSize: '0.72rem', opacity: 0.7 }}>{b.count} {unit} · {b.pct}%</div>
                                    </button>
                                );
                            })}
                    </div>
                    <div style={{ display: 'flex', gap: 16, marginBottom: '0.9rem', fontSize: '0.85rem', flexWrap: 'wrap' }}>
                        <span><strong>{t('U roku', 'On time')}:</strong> {fmtEur(summary.rok.u_roku.eur)} € ({fmtPct(summary.rok.u_roku.pct)}) · {fmtG(summary.rok.u_roku.fine_g)} g fine ({fmtPct(summary.rok.u_roku.pct_fine)}){faros ? <> · {summary.rok.u_roku.count} {unit} ({fmtPct(summary.rok.u_roku.pct_count)})</> : null}</span>
                        <span><strong>{t('Izvan roka', 'Overdue')}:</strong> {fmtEur(summary.rok.izvan_roka.eur)} € ({fmtPct(summary.rok.izvan_roka.pct)}) · {fmtG(summary.rok.izvan_roka.fine_g)} g fine ({fmtPct(summary.rok.izvan_roka.pct_fine)}){faros ? <> · {summary.rok.izvan_roka.count} {unit} ({fmtPct(summary.rok.izvan_roka.pct_count)})</> : null}</span>
                        {summary.rok.na_cekanju.count > 0 ? (
                            <span title={t('Narudžbe s dogovorenim datumom koji još nije prošao ili s oznakom "Na čekanju" (čeka se klijent). Nisu ni u roku ni zakašnjele.', 'Orders with an agreed date that has not passed yet, or marked "On hold" (waiting for the customer). Neither on time nor overdue.')}><strong>{t('Na čekanju', 'On hold')}:</strong> {fmtEur(summary.rok.na_cekanju.eur)} € ({fmtPct(summary.rok.na_cekanju.pct)}) · {fmtG(summary.rok.na_cekanju.fine_g)} g fine ({fmtPct(summary.rok.na_cekanju.pct_fine)}){faros ? <> · {summary.rok.na_cekanju.count} {unit} ({fmtPct(summary.rok.na_cekanju.pct_count)})</> : null}</span>
                        ) : null}
                        <span><strong>{t('Ukupno', 'Total')}:</strong> {fmtEur(summary.total_eur)} € · {fmtG(summary.total_fine_g)} g fine{summary.total_fine_ag_g > 0 ? <> (Ag {fmtG(summary.total_fine_ag_g)} g)</> : null} · {summary.total_count} {unitLong}{countStavke ? <> ({summary.total_narudzbi} {t('narudžbi', 'orders')})</> : null}</span>
                    </div>
                </>
            ) : null}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.7rem' }}>
                <select value={status} onChange={(e) => { setAgeBucket(''); setStatus(e.target.value); }} style={inputStyle}>
                    <option value="">{t('Svi statusi', 'All statuses')}</option>
                    <option value="none">{t('Neoznačeno', 'Untagged')}</option>
                    {/* Nazivi i legenda iz shared/orderStatus (Tonći, mail 01.10.2026). */}
                    {(['u_dolasku', 'pakirano', 'na_putu', 'poslano', 'imam_ju', 'isporuceno'] as const).map((k) => (
                        <option key={k} value={k} title={orderStatusTooltip(k, locale)}>{orderStatusLabel(k, locale)}</option>
                    ))}
                    <option value="grupa:smjesteno">{t('Smješteno + greška u Farosu (grupa)', 'Placed + Faros error (group)')}</option>
                    <option value="grupa:poslano">{t('Poslano + poslano klijentu + pakirano (grupa)', 'Sent + sent to customer + packed (group)')}</option>
                </select>
                <select value={ageBucket} onChange={(e) => { setStatus(''); setAgeBucket(e.target.value); }} style={inputStyle}>
                    <option value="">{t('Sve starosti', 'All ages')}</option>
                    <option value="0-10">0-10 {t('dana', 'days')}</option>
                    <option value="11-20">11-20 {t('dana', 'days')}</option>
                    <option value="21+">21+ {t('dana', 'days')}</option>
                    <option value="na_cekanju">{t('Na čekanju (rok produljen)', 'On hold (deadline extended)')}</option>
                </select>
                <input type="text" placeholder={t('Poslovnica ili šifra…', 'Branch or code…')} value={branch} onChange={(e) => setBranch(e.target.value)} style={{ ...inputStyle, minWidth: 140 }} />
                <input type="text" placeholder={t('Broj narudžbe…', 'Order number…')} value={search} onChange={(e) => setSearch(e.target.value)} style={{ ...inputStyle, minWidth: 160 }} />
                {(status || ageBucket || branch || search) ? (
                    <button type="button" onClick={() => { setStatus(''); setAgeBucket(''); setBranch(''); setSearch(''); }} style={{ ...inputStyle, cursor: 'pointer' }}>
                        {t('Poništi filtre', 'Clear filters')}
                    </button>
                ) : null}
                <button type="button" onClick={() => void exportXlsx()} style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
                    {t('Izvoz u Excel', 'Export to Excel')}
                </button>
            </div>

            <div style={{ fontSize: '0.8rem', opacity: 0.75, marginBottom: 6 }}>
                {busy
                    ? t('Učitavam…', 'Loading…')
                    : faros
                        ? `${total} ${t('narudžbi', 'orders')}${countStavke ? ` · ${sumStavki} ${t('stavki', 'lines')}` : ''} · ${fmtEur(sumIznos)} € ${t('punog iznosa', 'full amount')} · ${fmtG(sumFine)} g fine`
                        : amountIznos
                            ? `${total} ${t('narudžbi', 'orders')} · ${fmtEur(sumIznos)} € ${t('iznosa narudžbe', 'order amount')} · ${fmtG(sumFine)} g fine`
                            : `${total} ${t('narudžbi', 'orders')} · ${fmtEur(sumPredujam)} € ${t('predujma', 'advances')} · ${fmtG(sumFine)} g fine`}
            </div>

            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid rgba(var(--ad-soft-rgb),0.4)', textAlign: 'left' }}>
                            <th style={{ padding: '6px 8px' }}>ŠF</th>
                            <th style={{ padding: '6px 8px', cursor: 'pointer' }} onClick={() => toggleSort('poslovnica')}>{t('Poslovnica', 'Branch')}{sortArrow('poslovnica')}</th>
                            <th style={{ padding: '6px 8px', cursor: 'pointer' }} onClick={() => toggleSort('order_number')}>{t('Broj narudžbe', 'Order no.')}{sortArrow('order_number')}</th>
                            <th style={{ padding: '6px 8px', cursor: 'pointer' }} onClick={() => toggleSort('datum_unosa')}>{t('Datum unosa', 'Entry date')}{sortArrow('datum_unosa')}</th>
                            <th style={{ padding: '6px 8px', cursor: 'pointer', textAlign: 'right' }} onClick={() => toggleSort('starost_dana')}>{t('Starost', 'Age')}{sortArrow('starost_dana')}</th>
                            <th style={{ padding: '6px 8px', cursor: 'pointer', textAlign: 'right' }} onClick={() => toggleSort('stavki')}>{t('Stavki', 'Lines')}{sortArrow('stavki')}</th>
                            <th style={{ padding: '6px 8px', cursor: 'pointer', textAlign: 'right' }} onClick={() => toggleSort('fine_g')} title={t('Fine zlato u gramima, zbroj stavki narudžbe', 'Fine gold in grams, sum of order lines')}>{t('Fine g', 'Fine g')}{sortArrow('fine_g')}</th>
                            <th style={{ padding: '6px 8px', cursor: 'pointer', textAlign: 'right', fontWeight: amountIznos ? 800 : undefined }} onClick={() => toggleSort('iznos_eur')}>{t('Iznos', 'Amount')}{sortArrow('iznos_eur')}</th>
                            <th style={{ padding: '6px 8px', cursor: 'pointer', textAlign: 'right', fontWeight: amountIznos ? undefined : 800 }} onClick={() => toggleSort('predujam_eur')}>{t('Predujam', 'Advance')}{sortArrow('predujam_eur')}</th>
                            <th style={{ padding: '6px 8px', cursor: 'pointer' }} onClick={() => toggleSort('status_bis')}>{t('BIS status', 'BIS status')}{sortArrow('status_bis')}</th>
                            <th style={{ padding: '6px 8px' }}>{t('U roku?', 'On time?')}</th>
                            <th style={{ padding: '6px 8px' }}>{t('Novi rok / napomena', 'New deadline / note')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((o, i) => (
                            <tr key={o.order_number} onClick={() => setDrawer(o)} style={{ cursor: 'pointer', borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.15)', background: drawer?.order_number === o.order_number ? 'rgba(var(--ad-acc-rgb),0.12)' : i % 2 ? 'rgba(254,243,199,0.25)' : 'transparent' }}>
                                <td style={{ padding: '5px 8px' }}>{o.sf}</td>
                                <td style={{ padding: '5px 8px' }}>{o.poslovnica ?? '—'}{o.zlatna_stednja ? <span title={t('Zlatna štednja', 'Zlatna štednja')} style={{ marginLeft: 6, fontSize: '0.7rem', opacity: 0.6 }}>ZŠ</span> : null}</td>
                                <td style={{ padding: '5px 8px' }}>{o.order_number}</td>
                                <td style={{ padding: '5px 8px' }}>{o.datum_unosa ? String(o.datum_unosa).slice(0, 10) : '—'}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'right' }}>{o.starost_dana ?? '—'}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'right' }}>{o.stavki}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtG(o.fine_g)}{o.fine_ag_g > 0 ? <span style={{ fontSize: '0.7rem', opacity: 0.6, marginLeft: 4 }}>Ag {fmtG(o.fine_ag_g)}</span> : null}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'right' }}>{fmtEur(o.iznos_eur)}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'right' }}>{fmtEur(o.predujam_eur)}</td>
                                <td style={{ padding: '5px 8px', cursor: 'help' }} title={orderStatusTooltip(o.status_bis ?? 'none', locale)}>{o.status_bis_label ?? <em style={{ opacity: 0.6 }}>{t('bez oznake', 'untagged')}</em>}</td>
                                <td style={{ padding: '5px 8px' }}>{o.rok_status === 'na_cekanju' ? <span title={t('Na čekanju: rok produljen na zahtjev stranke', 'On hold: deadline extended at customer request')}>⏸</span> : o.u_roku ? '✅' : '⚠️'}</td>
                                <td style={{ padding: '5px 8px', fontSize: '0.8rem' }}>{[o.dogovoreni_datum ? String(o.dogovoreni_datum).slice(0, 10) : null, o.napomena].filter(Boolean).join(' · ') || ''}</td>
                            </tr>
                        ))}
                        {!busy && rows.length === 0 ? (
                            <tr><td colSpan={12} style={{ padding: '1rem', textAlign: 'center', opacity: 0.6 }}>{t('Nema narudžbi za odabrane filtre.', 'No orders for the selected filters.')}</td></tr>
                        ) : null}
                    </tbody>
                </table>
            </div>
            {drawer ? <OrderItemsDrawer order={drawer} locale={locale} onClose={() => setDrawer(null)} /> : null}
        </div>
    );
}

/**
 * N14: artikli narudzbe bez kolicina, s oznakom AU/AG. Isti naziv u vise redaka prikazuje se jednom.
 * Fine (Vedran 01.10.2026): uz artikl jedinicni fine i fine stavke (zbroj redaka istog naziva), dolje zbroj narudzbe.
 */
function OrderItemsDrawer({ order, locale, onClose }: { order: OpenOrderRow; locale: Locale; onClose: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const fmtG = (n: number) => n.toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 3 });
    const byKey = new Map<string, { metal: 'AU' | 'AG'; naziv: string; jed: number | null; fine: number }>();
    for (const it of order.items) {
        const key = `${it.metal}|${it.naziv}`;
        const jed = it.metal === 'AU' ? it.fine_jed_g : it.fine_ag_jed_g;
        const fine = it.metal === 'AU' ? it.fine_g : it.fine_ag_g;
        const cur = byKey.get(key);
        if (cur) cur.fine += fine;
        else byKey.set(key, { metal: it.metal, naziv: it.naziv, jed, fine });
    }
    const items = Array.from(byKey.values());
    const au = items.filter((it) => it.metal === 'AU').length;
    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 1000 }}>
            <aside onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(460px, 100%)', background: '#fff', padding: '1rem 1.1rem', overflowY: 'auto', boxShadow: '-2px 0 12px rgba(0,0,0,0.15)' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <h3 style={{ margin: 0, fontSize: '1rem' }}>{order.order_number}</h3>
                    <button type="button" onClick={onClose} style={{ marginLeft: 'auto', padding: '2px 10px', borderRadius: 6, cursor: 'pointer' }}>×</button>
                </div>
                <div style={{ fontSize: '0.8rem', opacity: 0.75, margin: '4px 0 10px' }}>
                    {order.poslovnica ?? '—'} · {order.datum_unosa ? String(order.datum_unosa).slice(0, 10) : '—'} · {order.status_bis_label ?? t('bez oznake', 'untagged')}
                </div>
                <div style={{ fontSize: '0.8rem', marginBottom: 6 }}>
                    {items.length} {t('artikala', 'items')} · AU {au} · AG {items.length - au} · <strong>{t('fine Au', 'fine Au')} {fmtG(order.fine_g)} g</strong>{order.fine_ag_g > 0 ? <> · {t('fine Ag', 'fine Ag')} {fmtG(order.fine_ag_g)} g</> : null}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid rgba(var(--ad-soft-rgb),0.4)', textAlign: 'left' }}>
                            <th style={{ padding: '4px 6px', width: 44 }}>{t('Metal', 'Metal')}</th>
                            <th style={{ padding: '4px 6px' }}>{t('Artikl', 'Item')}</th>
                            <th style={{ padding: '4px 6px', textAlign: 'right', whiteSpace: 'nowrap' }} title={t('Fine po komadu', 'Fine per unit')}>{t('Fine jed. g', 'Fine unit g')}</th>
                            <th style={{ padding: '4px 6px', textAlign: 'right', whiteSpace: 'nowrap' }} title={t('Fine stavke (zbroj)', 'Line fine (sum)')}>{t('Fine g', 'Fine g')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((it) => (
                            <tr key={`${it.metal}|${it.naziv}`} style={{ borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.15)' }}>
                                <td style={{ padding: '4px 6px', fontWeight: 700 }}>{it.metal}</td>
                                <td style={{ padding: '4px 6px' }}>{it.naziv}</td>
                                <td style={{ padding: '4px 6px', textAlign: 'right' }}>{it.jed === null ? '—' : fmtG(it.jed)}</td>
                                <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>{fmtG(it.fine)}</td>
                            </tr>
                        ))}
                        {items.length === 0 ? (
                            <tr><td colSpan={4} style={{ padding: '0.8rem', textAlign: 'center', opacity: 0.6 }}>{t('Narudžba nema stavki u Faros snapshotu.', 'Order has no items in the Faros snapshot.')}</td></tr>
                        ) : null}
                    </tbody>
                </table>
            </aside>
        </div>
    );
}
